import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, AlertCircle, User, Lock, RefreshCw } from 'lucide-react';
import axios from 'axios';

// Cấu hình địa chỉ API và Tên ứng dụng từ biến môi trường
const API_BASE = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000/api";
const APP_NAME = process.env.REACT_APP_APP_NAME || "XML SYSTEM";

const Login = () => {
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoading(true);

    try {
      // Sử dụng URLSearchParams vì FastAPI OAuth2 yêu cầu form-data
      const formData = new URLSearchParams();
      formData.append('username', loginData.username);
      formData.append('password', loginData.password);
      
      const res = await axios.post(`${API_BASE}/login`, formData, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });
      
      // 1. Lưu Token để xác thực các yêu cầu API sau này
      localStorage.setItem("admin_token", res.data.access_token);
      
      // 2. Lưu toàn bộ thông tin tài khoản (fullname, role, facility_id,...)
      // Lưu ý: Backend cần trả về object 'user' trong response
      if (res.data.user) {
        localStorage.setItem("user_info", JSON.stringify(res.data.user));
        console.log("Đã lưu thông tin người dùng:", res.data.user);
      }
      
      // 3. Chuyển hướng về trang chủ điều hành
      navigate('/dashboard'); 

    } catch (err) {
      console.error("Lỗi đăng nhập:", err);
      // Hiển thị lỗi chi tiết từ Backend hoặc thông báo mặc định
      setLoginError(err.response?.data?.detail || 'Sai tài khoản hoặc mật khẩu!');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f0f9ff] flex items-center justify-center p-6 font-sans relative overflow-hidden">
      
      {/* Hiệu ứng mờ ảo phía sau (Background Decoration) */}
      <div className="absolute inset-0 z-0 pointer-events-none filter blur-[100px] opacity-50">
          <div className="absolute w-[500px] h-[500px] bg-sky-300 rounded-full -top-20 -left-20 animate-pulse"></div>
          <div className="absolute w-[400px] h-[400px] bg-indigo-300 rounded-full bottom-0 right-0"></div>
      </div>

      {/* Khung đăng nhập Glassmorphism */}
      <form 
        onSubmit={handleLogin} 
        className="relative z-10 bg-white/80 backdrop-blur-2xl p-10 rounded-[40px] shadow-2xl shadow-sky-900/10 w-full max-w-md border border-white"
      >
        {/* Header của Form */}
        <div className="flex items-center gap-4 mb-8 pb-6 border-b border-slate-100">
          <div className="p-3 bg-sky-600 rounded-2xl shadow-lg shadow-sky-200">
            <ShieldCheck size={28} className="text-white"/>
          </div>
          <div>
            <h2 className="text-2xl font-black text-slate-900 tracking-tighter">{APP_NAME}</h2>
            <p className="text-slate-500 text-[10px] font-bold uppercase tracking-[0.2em]">Hệ thống giám định XML</p>
          </div>
        </div>

        {/* Thông báo lỗi nếu có */}
        {loginError && (
          <div className="bg-rose-50 text-rose-600 p-4 rounded-2xl mb-6 text-sm font-bold flex items-center gap-3 border border-rose-100 animate-in fade-in slide-in-from-top-2">
            <AlertCircle size={18}/> 
            {loginError}
          </div>
        )}

        {/* Các ô nhập liệu */}
        <div className="space-y-4">
          <div className="relative group">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-sky-600 transition-colors" size={18}/>
            <input 
              type="text" 
              required 
              className="w-full pl-12 pr-4 py-4 bg-white/50 border border-slate-200 rounded-2xl outline-none focus:ring-4 ring-sky-100 focus:border-sky-400 transition-all font-semibold placeholder:text-slate-300" 
              placeholder="Tên đăng nhập" 
              value={loginData.username} 
              onChange={(e) => setLoginData({...loginData, username: e.target.value})} 
            />
          </div>

          <div className="relative group">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-sky-600 transition-colors" size={18}/>
            <input 
              type="password" 
              required 
              className="w-full pl-12 pr-4 py-4 bg-white/50 border border-slate-200 rounded-2xl outline-none focus:ring-4 ring-sky-100 focus:border-sky-400 transition-all font-semibold placeholder:text-slate-300" 
              placeholder="Mật khẩu" 
              value={loginData.password} 
              onChange={(e) => setLoginData({...loginData, password: e.target.value})} 
            />
          </div>
        </div>

        {/* Nút bấm Đăng nhập */}
        <button 
          type="submit" 
          disabled={loading} 
          className="w-full mt-8 py-5 bg-slate-900 text-white rounded-[24px] font-black text-xs tracking-[0.1em] hover:bg-sky-600 transition-all shadow-xl shadow-slate-900/20 active:scale-95 disabled:bg-slate-300 flex items-center justify-center uppercase"
        >
          {loading ? (
            <RefreshCw size={20} className="animate-spin"/>
          ) : (
            'Đăng nhập hệ thống'
          )}
        </button>

        {/* Footer ghi chú */}
        <p className="text-center mt-8 text-slate-400 text-[10px] font-medium uppercase tracking-widest">
          Sóc Trăng Medical Solution © 2026
        </p>
      </form>
    </div>
  );
};

export default Login;